/**
 * The provenance vocabulary — glyph, label key and treatment per source.
 *
 * Kept out of `ProvenanceBadge.tsx` so the badge file exports a component and
 * nothing else, and so a test can import the table on its own to assert the
 * two rules that matter:
 *
 *  1. five provenances, five DIFFERENT glyphs (never colour-only, survives
 *     greyscale and print);
 *  2. not one of them uses a traffic-light token — provenance is not severity,
 *     and a reader must never learn to read one palette as the other.
 */
import { Calculator, CircleHelp, MapPin, PenLine, ScanEye } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import type { TranslationKey } from '@/i18n'
import type { Provenance } from '@/api/types'

export interface ProvenanceLook {
  icon: LucideIcon
  label: TranslationKey
  meaning: TranslationKey
  className: string
}

/** Every value the backend's `Provenance` literal can take. */
export const PROVENANCES: readonly Provenance[] = [
  'deterministisch',
  'llm',
  'gis',
  'manuell',
  'unbekannt',
]

export const PROVENANCE_LOOK: Record<Provenance, ProvenanceLook> = {
  // Read or computed from the form / the plan text. Solid, plain, factual.
  deterministisch: {
    icon: Calculator,
    label: 'facts.prov.deterministisch',
    meaning: 'facts.prov.deterministisch.meaning',
    className: 'border-border-strong bg-white text-ink-2',
  },
  // The model read it off a drawing. Dashed: derived, not certified — the same
  // visual language the report uses for indicative output.
  llm: {
    icon: ScanEye,
    label: 'facts.prov.llm',
    meaning: 'facts.prov.llm.meaning',
    className: 'border-dashed border-border-strong bg-surface-2 text-muted',
  },
  // A GIS layer answered. Info navy — never one of the traffic-light colours.
  gis: {
    icon: MapPin,
    label: 'facts.prov.gis',
    meaning: 'facts.prov.gis.meaning',
    className: 'border-signal-info/40 bg-signal-info-soft text-signal-info',
  },
  // A human corrected it. The strongest badge on the surface, on purpose.
  manuell: {
    icon: PenLine,
    label: 'facts.prov.manuell',
    meaning: 'facts.prov.manuell.meaning',
    className: 'border-accent bg-accent-soft text-accent',
  },
  // No provenance arrived. Said out loud rather than filled in — calling it
  // `deterministisch` would be exactly the guess this surface exists to stop.
  unbekannt: {
    icon: CircleHelp,
    label: 'facts.prov.unbekannt',
    meaning: 'facts.prov.unbekannt.meaning',
    className: 'border-dotted border-border-strong bg-white text-muted-2',
  },
}
